import {
  appInvestmentsToProfileInvestments,
  appAccountsToProfileAccounts,
  convertToChosenInvestments,
  getResidentialAddressForEnabled,
  filterMailingAddressForEnabled,
  setInvestmentFundName
} from '../shared/services/util';
import { isEmpty, withDefault, APP_NAME, APP_VERSION } from '../../core/util';
import {
  applicationStatusTypes,
  accountTypes,
  setTaxIDType,
  parsePhone,
  setRJCustomerPhones,
  autoSplitPercents,
  rolloverTypes,
  allocateFunds,
  getMailingAddressDifferentFlag
} from '../shared/utils';
import { PartialStateStore } from '../../core/types/application-state';
import {
  StaticExistingAccount,
  StaticInvestment
} from '../../core/types/investment';
import { DeepPartialProfileArgs } from '../../core/types/return-journey-profile';

export const cleanPayloadOfCustodianData = (appData): any => {
  /* eslint-disable no-param-reassign */
  if (appData.custodian) {
    delete appData.custodian;
  }

  return appData;
  /* eslint-enable no-param-reassign */
};

export const getExistingAgreementID = (ticker, app): string => {
  const { existingAccounts } = app;
  const foundAgreementFund = existingAccounts?.filter(
    account => account.investmentVehicle.ticker === ticker
  );
  return foundAgreementFund[0]?.agreementID;
};

export const mapUpdatedInvestments = (amount, app, chosen, result): any => {
  let updatedChosenInvestments;
  let existingInvestments = [];

  // logic for mixed case of new + existing funds
  // data has gone through cart state and allocations are set by BYOP path
  // applicable for editing from review or navigating back to existing funds from BYOP
  if (app?.newInvestments?.length) {
    const mixedInvestments = (app?.newInvestments || []).concat(chosen || []);
    // determine amount and percentages based on already existing/new investments
    // allocation is needed incase user doesn't want to add any new funds
    // and directly taken to BYOP review page
    const mixedInvestmentAllocations = autoSplitPercents(
      mixedInvestments,
      amount
    );

    const existingFundsTickers = chosen?.map(account => account?.ticker) || [];
    const filteredExistingsInvestments = mixedInvestmentAllocations.filter(
      item => existingFundsTickers.includes(item.ticker)
    );
    const filteredNewInvestments = mixedInvestmentAllocations.filter(
      item => !existingFundsTickers.includes(item.ticker)
    );

    // update existing investments accordingly
    existingInvestments = filteredExistingsInvestments.map(
      ({ ticker, trpCode, fundingDetails }) => {
        const chosenAgreement = chosen.find(
          ({ agreementID, ticker: chosenTicker }) => {
            return chosenTicker === ticker && agreementID;
          }
        );

        return {
          investmentVehicle: {
            ticker,
            trpCode
          },
          agreementID:
            chosenAgreement?.agreementID || getExistingAgreementID(ticker, app),
          fundingDetails
        };
      }
    );

    const updatedNewInvestments = filteredNewInvestments;

    // needs to have percent and amount included so allocations will display correctly
    // updated chosen investments with new investments if present (otherwise they will be dropped on BYOP)
    updatedChosenInvestments = chosen
      .map(fund => {
        const matchedFund = mixedInvestmentAllocations.find(
          mixedInv => mixedInv.ticker === fund.ticker
        );
        return {
          ...fund,
          ...(matchedFund && {
            percent: matchedFund?.fundingDetails?.fundingAmountPercentage,
            amount: matchedFund?.fundingDetails?.fundingAmount
          })
        };
      })
      .concat(updatedNewInvestments);

    return {
      existingInvestments,
      updatedNewInvestments,
      updatedChosenInvestments
    };
  }

  // splits fund allocation when user choses
  // EXISTING_ONLY funds and skip BYOP screen
  const existingFundAllocations =
    result === 'ExistingAgreementScreen'
      ? autoSplitPercents(chosen, amount)
      : allocateFunds(chosen);

  existingInvestments = existingFundAllocations.map(
    ({ ticker, trpCode, fundingDetails }) => {
      const chosenAgreement = chosen.find(
        ({ agreementID, ticker: chosenTicker }) => {
          return chosenTicker === ticker && agreementID;
        }
      );

      return {
        investmentVehicle: {
          ticker,
          trpCode
        },
        agreementID:
          chosenAgreement?.agreementID || getExistingAgreementID(ticker, app),
        fundingDetails
      };
    }
  );

  // update chosen investments with EXISTING_ONLY fund allocations
  // these allocations are done special due to not passing through BYOP
  updatedChosenInvestments = chosen.map(fund => {
    const matchedFund = existingFundAllocations.find(
      existingFund => existingFund.ticker === fund.ticker
    );
    return {
      ...fund,
      ...(matchedFund && {
        percent: matchedFund?.fundingDetails?.fundingAmountPercentage,
        amount: matchedFund?.fundingDetails?.fundingAmount
      })
    };
  });

  return {
    existingInvestments,
    updatedChosenInvestments
  };
};

export const parseRolloverExistingInvestments = (
  profileInvestments
): StaticExistingAccount[] | [] => {
  if (!profileInvestments || !profileInvestments.length) return [];
  const parsedExistingInvestments = profileInvestments.filter(
    inv => inv.agreementID && inv.agreementID !== ''
  );

  const existingAccounts = parsedExistingInvestments.map(
    ({
      ticker,
      trpCode,
      fundingAmount,
      fundingAmountPercentage,
      agreementID
    }) => ({
      investmentVehicle: {
        ticker,
        trpCode
      },
      agreementID,
      fundingDetails: {
        fundingAmount,
        fundingAmountPercentage
      }
    })
  );

  return existingAccounts;
};

export const parseRolloverNewInvestments = (
  profileInvestments
): StaticInvestment[] => {
  if (!profileInvestments || !profileInvestments.length) return [];
  const parsedNewInvestments = profileInvestments.filter(
    inv => !inv.agreementID || inv.agreementID === ''
  );

  const newInvestments = parsedNewInvestments.map(
    ({
      fundName,
      ticker,
      trpCode,
      fundingAmount,
      fundingAmountPercentage
    }) => ({
      fundName,
      ticker,
      trpCode,
      fundingDetails: {
        fundingAmount,
        fundingAmountPercentage
      }
    })
  );

  return newInvestments;
};

export const convertAppDataToProfileDataRollover = (
  store: PartialStateStore
): DeepPartialProfileArgs => {
  const { app, chosenInvestments, progressStepText } = store;

  // add fund name to investment allocations; needed for form request
  const updatedInvestmentAllocations = setInvestmentFundName(
    chosenInvestments,
    app?.newInvestments
  );

  const newInvestments = appInvestmentsToProfileInvestments(
    updatedInvestmentAllocations
  );

  const existingAccounts = appAccountsToProfileAccounts(app?.existingAccounts);

  // TODO [2020-05-25]: Let's make this a util to combine new + existing investments for profile service
  const investmentAllocations = [...existingAccounts, ...newInvestments];

  return {
    applicationID: withDefault(app?.profileApplicationID, ''),
    emailAddress: app?.customer?.primaryEmailAddress,
    applicationStatus: withDefault(
      app?.applicationStatus,
      applicationStatusTypes.IN_PROGRESS
    ),
    investmentAllocations,
    ...(app?.custodian?.custodianName && {
      custodianInformation: [
        {
          custodianName: app?.custodian?.custodianName,
          custodianID: app?.custodian?.custodianID
        }
      ]
    }),
    accountType: accountTypes.ROLLOVER,
    investAmount: withDefault(store?.estimatedRolloverAmount, store?.amount),
    firstName: app?.customer?.name?.firstName,
    lastName: app?.customer?.name?.lastName,
    middleName: withDefault(app?.customer?.name?.middleName, ''),
    residentialAddress: {
      street1: app?.customer?.residentialAddress?.street1,
      street2: app?.customer?.residentialAddress?.street2,
      state: app?.customer?.residentialAddress?.state,
      zip: app?.customer?.residentialAddress?.zip,
      city: app?.customer?.residentialAddress?.city
    },
    mailingAddress: {
      street1: app?.customer?.mailingAddress?.street1,
      street2: app?.customer?.mailingAddress?.street2,
      state: app?.customer?.mailingAddress?.state,
      zip: app?.customer?.mailingAddress?.zip,
      city: app?.customer?.mailingAddress?.city
    },
    citizenship: withDefault(app?.customer?.citizenship, 'UNKNOWN'),
    dateOfBirth: app?.customer?.dob,
    ...(store?.formerEmployerName && {
      formerEmployer: store?.formerEmployerName
    }),
    taxID: withDefault(app?.customer?.taxID, ''),
    phoneNumbers:
      app?.customer?.phones?.map(({ areaCode, exchange, lastFour, type }) => ({
        phoneNumber: `(${areaCode}) ${exchange}-${lastFour}`,
        phoneNumberType: type
      })) || [],
    investmentSelectionPath: app?.investmentMethod,
    rolloverType: withDefault(store?.rolloverType, ''),
    residentialAddressDifferentFromMailing: withDefault(
      store?.mailingAddressDifferent,
      false
    ),
    // ToDo: Add termIDs on the review page submission
    termsIDs: withDefault(
      app?.termsIDs?.map(id => id?.toString()),
      []
    ),
    consumerVersion: APP_VERSION,
    consumerName: APP_NAME,
    progressStepText,
    progressStep: withDefault(store?.progressStep, 0)
  };
};

/* eslint-disable complexity */
export const convertProfileDataToAppDataRollover = async (
  profile: DeepPartialProfileArgs,
  store: PartialStateStore
): Promise<PartialStateStore> => {
  // account for logged in user customer data
  // customer data should take precedence over journey profile customer data
  const { app, enabled } = store;
  const phones = setRJCustomerPhones(
    profile?.phoneNumbers,
    app?.customer?.phones
  );

  return {
    accountType: profile?.accountType,
    estimatedRolloverAmount: profile?.investAmount,
    amount: profile?.investAmount,
    app: {
      customer: enabled
        ? {
            ...app?.customer,
            citizenship: profile?.citizenship,
            taxIDType: setTaxIDType(profile?.citizenship),
            residentialAddress: getResidentialAddressForEnabled(
              app?.customer?.residentialAddress,
              profile?.residentialAddress
            ),
            // pull email, phone and mailing address from profile service to
            // account for RPS only users submitting via FONASU RJ
            // since the customer call while doing RJ is not pulling them currently
            mailingAddress: withDefault(
              profile?.mailingAddress,
              filterMailingAddressForEnabled(app?.customer?.mailingAddress)
            ),
            primaryEmailAddress: withDefault(
              profile?.emailAddress,
              app?.customer?.primaryEmailAddress
            ),
            phones
          }
        : {
            name: {
              firstName: profile?.firstName,
              middleName: withDefault(profile?.middleName, ''),
              lastName: profile?.lastName
            },
            citizenship: profile?.citizenship,
            dob: profile?.dateOfBirth,
            taxID: profile?.taxID,
            taxIDType: setTaxIDType(profile?.citizenship),
            residentialAddress: {
              street1: profile?.residentialAddress?.street1,
              street2: profile?.residentialAddress?.street2,
              state: profile?.residentialAddress?.state,
              zip: profile?.residentialAddress?.zip,
              city: profile?.residentialAddress?.city
            },
            mailingAddress: {
              street1: profile?.mailingAddress?.street1,
              street2: profile?.mailingAddress?.street2,
              state: profile?.mailingAddress?.state,
              zip: profile?.mailingAddress?.zip,
              city: profile?.mailingAddress?.city
            },
            primaryEmailAddress: profile?.emailAddress,
            // [{phoneNumberType: string, phoneNumber: string}]
            // needs to be converted to ->
            // [{ areaCode: string, exchange: string, lastFour: string, type: string }]
            //
            // TODO [2020-06-20]: This is a big pain why can't backend use same format
            phones: profile?.phoneNumbers?.map(
              ({ phoneNumberType: type = '', phoneNumber = '' }) => ({
                ...parsePhone(phoneNumber),
                type
              })
            )
          },
      existingAccounts: parseRolloverExistingInvestments(
        profile?.investmentAllocations
      ),
      newInvestments: parseRolloverNewInvestments(
        profile?.investmentAllocations
      ),
      investmentMethod: profile?.investmentSelectionPath,
      profileApplicationID: profile?.applicationID,
      termsIDs: withDefault(
        profile?.termsIDs?.map(id => id?.toString()),
        []
      ),
      ...(profile?.custodianInformation?.[0]?.custodianName?.length &&
        profile?.rolloverType === rolloverTypes.DIRECT && {
          custodian: {
            custodianName: profile?.custodianInformation?.[0]?.custodianName,
            ...(!isEmpty(profile?.custodianInformation?.[0]?.custodianID) && {
              custodianID: profile?.custodianInformation?.[0]?.custodianID
            })
          }
        })
    },
    mailingAddressDifferent: getMailingAddressDifferentFlag(
      enabled,
      store,
      profile
    ),
    rolloverType: profile?.rolloverType,
    ...(profile?.formerEmployer && {
      formerEmployerName: withDefault(profile?.formerEmployer, '')
    }),
    chosenInvestments: await convertToChosenInvestments(
      profile?.investmentAllocations,
      profile?.investmentSelectionPath,
      app?.customer?.dob,
      enabled
    ),
    progressStepText: profile?.progressStepText,
    progressStep: withDefault(profile?.progressStep, 0),
    consumerVersion: profile?.consumerVersion,
    consumerName: profile?.consumerName
  };
};
/* eslint-enable complexity */
